import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, StatusBadge, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FileText, CheckCircle2, Circle, Upload } from "lucide-react";
import { toast } from "sonner";

export default function MyDocuments() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [uploadFor, setUploadFor] = useState(null); // the document row currently being uploaded

  const { data, isLoading } = useQuery({ queryKey: ["myDocs"], queryFn: async () => (await api.get("/my/documents")).data });

  const upload = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", fileRef.current.files[0]);
      fd.append("document_type", uploadFor.document_type);
      return (await api.post("/my/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => {
      toast.success("Document submitted — HR will review it");
      qc.invalidateQueries({ queryKey: ["myDocs"] });
      setUploadFor(null);
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const openUpload = (d) => { setUploadFor(d); if (fileRef.current) fileRef.current.value = ""; };
  const submit = () => {
    if (!fileRef.current?.files?.length) { toast.error("Choose a file first"); return; }
    upload.mutate();
  };

  if (isLoading) return <Loading />;
  return (
    <div data-testid="my-documents">
      <PageHeader overline="Records" title="My Documents" icon={FileText}
        subtitle="Upload each document once. After submission, HR holds the file — contact HR if a document needs to be viewed, corrected, or re-submitted." />
      {(data || []).length === 0 ? (
        <EmptyState icon={FileText} title="No document checklist configured" hint="HR will set up the required document list." />
      ) : (
        <Card className="border-slate-200 shadow-none overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead className="overline text-slate-500">Document</TableHead>
              <TableHead className="overline text-slate-500">Required</TableHead>
              <TableHead className="overline text-slate-500">Status</TableHead>
              <TableHead className="overline text-slate-500">Expiry</TableHead>
              <TableHead className="text-right overline text-slate-500">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.map((d) => (
                <TableRow key={d.document_type} className="table-row-i" data-testid={`my-doc-${d.document_type}`}>
                  <TableCell className="font-medium text-navy">{d.document_type}</TableCell>
                  <TableCell className="text-slate-500">{d.required ? "Yes" : "—"}</TableCell>
                  <TableCell>
                    {d.uploaded ? (
                      <span className="inline-flex items-center gap-1.5 text-emerald-700">
                        <CheckCircle2 className="h-4 w-4" /> Submitted
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-slate-400">
                        <Circle className="h-4 w-4" /> Pending
                      </span>
                    )}
                  </TableCell>
                  <TableCell>{d.expiry_status ? <StatusBadge status={d.expiry_status} /> : "—"}</TableCell>
                  <TableCell className="text-right">
                    {!d.uploaded && (
                      <Button size="sm" variant="outline" onClick={() => openUpload(d)} data-testid={`upload-mydoc-${d.document_type}`}>
                        <Upload className="h-4 w-4 mr-1" /> Upload
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={!!uploadFor} onOpenChange={(o) => !o && setUploadFor(null)}>
        <DialogContent className="bg-white" data-testid="my-doc-upload-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl text-navy">Upload {uploadFor?.document_type}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">
            Once submitted, this document is sent to HR and you won't be able to view, download, or replace it yourself.
            If you upload the wrong file, ask HR/Super Admin to reset it so you can try again.
          </p>
          <div>
            <Label>
              File * (PDF / image / Word, max {uploadFor?.max_size_mb ? `${uploadFor.max_size_mb} MB` : "15 MB"})
            </Label>
            <Input type="file" ref={fileRef} className="mt-1" data-testid="my-doc-file" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadFor(null)}>Cancel</Button>
            <Button className="bg-navy hover:bg-navy-700" disabled={upload.isPending} onClick={submit} data-testid="submit-my-doc-upload">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
